import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';

import {
  CheckCircle,
  FloppyDisk,
  Key,
  ToggleLeft,
  ToggleRight,
  Trash,
  WarningCircle
} from '@phosphor-icons/react';
const OrgLLMSettings = ({ readOnly = false }) => {
  const { activeOrganization } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [config, setConfig] = useState({
    llm_provider: 'OpenAI',
    llm_api_key: '',
    default_model: 'gpt-4o',
    has_key: false,
    llm_key_enabled: false
  });

  const providers = [
    { value: 'OpenAI', label: 'OpenAI', enabled: true },
    { value: 'Anthropic', label: 'Anthropic (Claude)', enabled: true },
    { value: 'Google', label: 'Google (Gemini)', enabled: true },
    { value: 'Azure', label: 'Azure OpenAI', enabled: true },
    { value: 'Mistral', label: 'Mistral AI', enabled: true }
  ];

  const modelsByProvider = {
    'OpenAI': [
      { value: 'gpt-4o', label: 'GPT-4o' },
      { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
      { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
      { value: 'gpt-4', label: 'GPT-4' },
      { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' }
    ],
    'Anthropic': [
      { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4' },
      { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
      { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
      { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' }
    ],
    'Google': [
      { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
      { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
      { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
      { value: 'gemini-pro', label: 'Gemini Pro' }
    ],
    'Azure': [
      { value: 'gpt-4o', label: 'GPT-4o (Azure)' },
      { value: 'gpt-4', label: 'GPT-4 (Azure)' },
      { value: 'gpt-35-turbo', label: 'GPT-3.5 Turbo (Azure)' }
    ],
    'Mistral': [
      { value: 'mistral-large-latest', label: 'Mistral Large' },
      { value: 'mistral-medium-latest', label: 'Mistral Medium' },
      { value: 'mistral-small-latest', label: 'Mistral Small' },
      { value: 'open-mixtral-8x22b', label: 'Mixtral 8x22B' }
    ]
  };

  const availableModels = modelsByProvider[config.llm_provider] || modelsByProvider['OpenAI'];

  useEffect(() => {
    if (activeOrganization) {
      fetchConfig();
    }
  }, [activeOrganization]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/llm-config`,
        { method: 'GET' },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setConfig({
          llm_provider: data.llm_provider || 'OpenAI',
          llm_api_key: '',  // Never show the actual key
          default_model: data.default_model || 'gpt-4o',
          has_key: data.has_key,
          llm_key_enabled: data.llm_key_enabled || false
        });
      }
    } catch (error) {
      console.error('Error fetching LLM config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config.llm_api_key) {
      setMessage({ type: 'error', text: 'Please enter an API key' });
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/llm-config`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            llm_provider: config.llm_provider,
            llm_api_key: config.llm_api_key,
            default_model: config.default_model
          })
        },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'LLM configuration saved successfully' });
        setConfig(prev => ({ ...prev, has_key: true, llm_api_key: '' }));
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to save configuration' });
      }
    } catch (error) {
      console.error('Error saving LLM config:', error);
      setMessage({ type: 'error', text: 'Failed to save configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the LLM API key? All agents will stop working unless they have individual keys.')) {
      return;
    }

    try {
      setSaving(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/llm-config`,
        { method: 'DELETE' },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'LLM configuration deleted successfully' });
        setConfig({ llm_provider: 'OpenAI', llm_api_key: '', default_model: 'gpt-4o', has_key: false, llm_key_enabled: false });
      }
    } catch (error) {
      console.error('Error deleting LLM config:', error);
      setMessage({ type: 'error', text: 'Failed to delete configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    const newEnabled = !config.llm_key_enabled;
    try {
      setToggling(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/llm-config/toggle`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled: newEnabled })
        },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        setConfig(prev => ({ ...prev, llm_key_enabled: newEnabled }));
        setMessage({
          type: 'success',
          text: newEnabled
            ? 'LLM key enabled — agent tasks will use your key instead of credits'
            : 'LLM key disabled — agent tasks will use platform credits'
        });
      } else {
        const err = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: err.detail || 'Failed to toggle LLM key' });
      }
    } catch (error) {
      console.error('Error toggling LLM key:', error);
      setMessage({ type: 'error', text: 'Failed to toggle LLM key' });
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#111111] rounded-xl p-6 border border-[rgba(255,255,255,0.05)]">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-[#111111] rounded-xl p-6 border border-[rgba(255,255,255,0.05)]">
      <div className="flex items-center gap-3 mb-4">
        <Key className="text-[#6366F1]" size={24} />
        <div>
          <h3 className="text-lg font-semibold text-white">LLM API Key (Bring Your Own Key)</h3>
          <p className="text-sm text-gray-400 mt-1">
            Used for running workers in this organisation. Platform tasks (building agents, assessments) still use credits.
          </p>
        </div>
      </div>

      {message.text && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success' 
            ? 'bg-green-500/10 border border-green-500/20 text-green-400'
            : 'bg-red-500/10 border border-red-500/20 text-red-400'
        }`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <WarningCircle size={16} />}
          <span className="text-sm">{message.text}</span>
        </div>
      )}

      {/* LLM Key Toggle — only shown when a key is saved */}
      {config.has_key && (
        <div className="mb-4 p-4 rounded-xl bg-white/[0.03] border border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
              config.llm_key_enabled ? 'bg-emerald-500/15' : 'bg-white/5'
            }`}>
              <Key size={18} className={config.llm_key_enabled ? 'text-emerald-400' : 'text-gray-500'} />
            </div>
            <div>
              <p className="text-sm font-medium text-white">Use own LLM key for agent tasks</p>
              <p className="text-xs text-gray-500">
                {config.llm_key_enabled 
                  ? 'Agent executions use your API key — no credits consumed' 
                  : 'Agent executions use platform credits'}
              </p>
            </div>
          </div>
          <button
            onClick={handleToggle}
            disabled={toggling || readOnly}
            data-testid="llm-key-toggle"
            className="flex-shrink-0 disabled:opacity-50"
          >
            {config.llm_key_enabled ? (
              <ToggleRight size={36} weight="fill" className="text-emerald-400" />
            ) : (
              <ToggleLeft size={36} weight="fill" className="text-gray-600" />
            )}
          </button>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Provider
          </label>
          <select
            value={config.llm_provider}
            onChange={(e) => {
              const newProvider = e.target.value;
              setConfig(prev => ({ 
                ...prev, 
                llm_provider: newProvider,
                default_model: modelsByProvider[newProvider]?.[0]?.value || 'gpt-4o'
              }));
            }}
            disabled={readOnly}
            className={`w-full px-4 py-2 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)] rounded-lg text-white focus:outline-none focus:border-[#6366F1] ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {providers.map(provider => (
              <option 
                key={provider.value} 
                value={provider.value}
                disabled={!provider.enabled}
              >
                {provider.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            API Key
          </label>
          <input
            type="password"
            value={config.llm_api_key}
            onChange={(e) => setConfig(prev => ({ ...prev, llm_api_key: e.target.value }))}
            placeholder={config.has_key ? "sk-**** (already configured)" : "sk-..."}
            disabled={readOnly}
            className={`w-full px-4 py-2 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)] rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#6366F1] ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
          />
          <p className="text-xs text-gray-500 mt-1">
            {config.has_key 
              ? 'Leave empty to keep existing key, or enter a new key to replace it'
              : 'Your API key will be encrypted and stored securely'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Default Model
          </label>
          <select
            value={config.default_model}
            onChange={(e) => setConfig(prev => ({ ...prev, default_model: e.target.value }))}
            disabled={readOnly}
            className={`w-full px-4 py-2 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)] rounded-lg text-white focus:outline-none focus:border-[#6366F1] ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {availableModels.map(model => (
              <option key={model.value} value={model.value}>
                {model.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Default model for all agents in this organisation
          </p>
        </div>

        {!readOnly && (
        <div className="flex gap-3 pt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#6366F1] hover:bg-[#5558E3] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FloppyDisk size={16} />
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
          
          {config.has_key && (
            <button
              onClick={handleDelete}
              disabled={saving}
              className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash size={16} />
            </button>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default OrgLLMSettings;
